import { atom } from 'jotai';
import { RESET } from 'jotai/utils';
import axios, { isAxiosError, AxiosResponse } from 'axios';
import i18n from '@/_services/i18n';

import {
  customIdFieldAtom,
  loginCodeAtom,
  loginRequestAtom,
  loginStatusAtom,
  userAtom,
  loginErrorAtom,
} from '@/_atoms/auth';
import { respondentAtom, variantSelectionsAtom } from '@/_atoms/respondent';
import { answerMapAtom } from '@/_atoms/answer';
import { demographicMapAtom } from '@/_atoms/demographic';
import { languageIdAtom } from '@/_atoms/language';
import { progressionsAtom } from '@/_atoms/progress';
import { saveRespondentAtom } from '@/_atoms/respondentAction';

import { toAnswerMap } from '@/_helpers/answerHelpers';
import { toDemographicMap } from '@/_helpers/demographicHelpers';
import {
  captureLogin,
  parseLoginErrorPayload,
  storeSessionInfo,
  updatePreviousStatusCodes,
} from '@/_helpers/loginHelpers';

import { answersGetQuery } from '@/_queries/answersQueries';
import { demographicsGetQuery } from '@/_queries/demographicsQueries';
import { loginGetQuery, loginPostQuery } from '@/_queries/loginQueries';
import { progressionsGetQuery } from '@/_queries/progressionsQueries';
import { respondentGetQuery } from '@/_queries/respondentQueries';

import type {
  Login,
  LoginCode,
  LoginStatus,
  LoginRequest,
  LoginFailure,
} from '@/types/auth';
import type { Respondent } from '@/types/respondent';

/**
 * Action atom: submit/login (or resume an existing session if a loginId exists).
 */
export const loginActionAtom = atom(
  null,
  async (get, set, loginId: string | null = null): Promise<Login | undefined> => {
    const request: LoginRequest = get(loginRequestAtom);

    // Set "pending/loading"
    set(loginStatusAtom, (prev: LoginStatus) => ({
      currentStatusCode: 'PL',
      previousStatusCodes: updatePreviousStatusCodes(prev),
      errorMessage: '',
    }));
    set(loginErrorAtom, null);

    try {
      // GET if we have an existing session id; otherwise POST the login request
      const response: AxiosResponse<Login> = loginId ? await loginGetQuery(loginId) : await loginPostQuery(request);
      const login: Login = response.data;

      // Normalize loginCode.value (fall back to submitted code value if missing)
      const loginCode: LoginCode = {
        ...login.loginCode,
        value: login.loginCode?.value || request.loginCodeValue || '',
      };

      const respondentId: string | undefined = loginCode?.respondent;

      // Sanity check: must have respondent on the loginCode
      if (!respondentId) {
        const message = 'Login code is missing respondent information';
        console.error({ loginCode, message });
        set(loginErrorAtom, new Error(message));
        // Set a failure status with a minimal message
        set(loginStatusAtom, (prev: LoginStatus) => ({
          currentStatusCode: 'ER',
          previousStatusCodes: updatePreviousStatusCodes(prev),
          errorMessage: message,
        }));
        return;
      }

      // Success status + session + analytics
      set(loginCodeAtom, loginCode);
      // Clear previous status codes upon successful login
      set(loginStatusAtom, { currentStatusCode: login.status, errorMessage: '', previousStatusCodes: [] });
      storeSessionInfo(login.id, login.token?.access, login.token?.refresh);
      captureLogin(login, loginId);

      // Hydrate related state in parallel
      const [respondentRes, answersRes, demographicsRes, progressionsRes] = await Promise.all([
        respondentGetQuery(respondentId),
        answersGetQuery(respondentId),
        demographicsGetQuery(respondentId),
        progressionsGetQuery(respondentId),
      ]);

      const respondent: Respondent = respondentRes.data;

      // Choose language: respondent.language → onlyAvailableLanguage → null
      const onlyAvailableLanguage: string | null = loginCode.languages?.length === 1 ? loginCode.languages[0] : null;
      const lang: string | null = respondent.language ?? onlyAvailableLanguage ?? null;

      // Update respondent (persist if we just assigned a single available language)
      set(respondentAtom, { ...respondent, language: respondent.language ?? onlyAvailableLanguage });
      if (respondent.variantSelections) set(variantSelectionsAtom, respondent.variantSelections);
      if (!respondent.language && onlyAvailableLanguage) await set(saveRespondentAtom);

      // Language state + i18n + localStorage (guard SSR)
      set(languageIdAtom, lang);
      if (lang) {
        void i18n.changeLanguage(lang);
        if (typeof window !== 'undefined') localStorage.setItem('i18nextLng', lang);
      }

      // Update the rest of the state
      set(userAtom, respondent.user);
      set(loginRequestAtom, RESET);
      set(answerMapAtom, toAnswerMap(answersRes.data.results));
      set(demographicMapAtom, toDemographicMap(demographicsRes.data.results));
      set(progressionsAtom, progressionsRes.data.results);

      return login;
    } catch (error: unknown) {
      // Swallow canceled requests
      if (axios.isCancel(error)) return;

      if (isAxiosError(error)) {
        const payload: LoginFailure = parseLoginErrorPayload(
          error.response?.data,
          'ER',
          'Something went wrong. Please try again.'
        );

        // Update status to the server-provided code (fallback 'ER') and append history
        set(loginStatusAtom, (prev: LoginStatus) => ({
          currentStatusCode: payload.statusCode,
          previousStatusCodes: updatePreviousStatusCodes(prev),
          errorMessage: payload.message,
        }));

        // Server can tell us to prompt for a custom id field
        if (payload.customIdField) {
          set(customIdFieldAtom, payload.customIdField);
        }
        set(loginErrorAtom, error);
      } else {
        // Unexpected error: log and surface a generic message
        console.error({ 'Unexpected login error': error });
        set(loginErrorAtom, error instanceof Error ? error : new Error('Login failed.'));
        set(loginStatusAtom, (prev: LoginStatus) => ({
          currentStatusCode: 'ER',
          previousStatusCodes: updatePreviousStatusCodes(prev),
          errorMessage: 'Something went wrong. Please try again.',
        }));
      }

      return undefined;
    }
  }
);